import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Api from "../../Api";
import classes from "./npay.module.css";

// Интервал поллинга статуса оплаты.
const STATUS_POLL_INTERVAL_MS = 5000;

// Финальные статусы — на них останавливаем таймер и поллинг.
const FINAL_STATUSES = ["SUCCESS", "DECLINED", "EXPIRED"];

function formatTime(totalSeconds) {
    if (totalSeconds == null || totalSeconds < 0) {
        return "00:00";
    }
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return (
        (m < 10 ? "0" : "") + m + ":" + (s < 10 ? "0" : "") + s
    );
}

function formatMoney(value) {
    if (value == null || isNaN(value)) {
        return null;
    }
    return Number(value).toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

// Публичная страница оплаты NPay: QR + таймер + сумма (₽ и KGS) + инструкция по Сберу.
// Открывается плательщиком по ссылке {front}/npay/{token} (token — неугадываемый).
export default function NPay() {
    const { token } = useParams();
    const api = new Api({});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [status, setStatus] = useState("PROCESSING");
    const [remainingSeconds, setRemainingSeconds] = useState(null);

    const timerIntervalRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const expireTsRef = useRef(null);

    const stopTimer = useCallback(() => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    // Тикаем обратный отсчёт до expireTs (если задан).
    const recomputeRemaining = useCallback(() => {
        if (expireTsRef.current == null) {
            setRemainingSeconds(null);
            return;
        }
        const now = Math.floor(Date.now() / 1000);
        const left = expireTsRef.current - now;
        setRemainingSeconds(left > 0 ? left : 0);
        if (left <= 0) {
            stopTimer();
            setStatus((prev) => (FINAL_STATUSES.includes(prev) ? prev : "EXPIRED"));
        }
    }, [stopTimer]);

    const applyResponse = useCallback(
        (resp) => {
            if (!resp || resp.ok === false) {
                setError((resp && resp.error && resp.error.message) || "Ошибка загрузки платежа");
                return;
            }
            setData(resp);
            if (resp.status) {
                setStatus(resp.status);
            }
            expireTsRef.current = resp.expireAtTs != null ? resp.expireAtTs : null;
            recomputeRemaining();

            if (resp.status && FINAL_STATUSES.includes(resp.status)) {
                stopPolling();
                stopTimer();
            }
        },
        [recomputeRemaining, stopPolling, stopTimer]
    );

    const fetchData = useCallback(async () => {
        try {
            const resp = await api.getNpayPage(token);
            applyResponse(resp);
        } catch (e) {
            setError("Не удалось загрузить данные оплаты");
        }
    }, [token, applyResponse]); // eslint-disable-line react-hooks/exhaustive-deps

    // Первичная загрузка + запуск таймера и поллинга.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await api.getNpayPage(token);
                if (cancelled) {
                    return;
                }
                applyResponse(resp);
            } catch (e) {
                if (!cancelled) {
                    setError("Не удалось загрузить данные оплаты");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        timerIntervalRef.current = setInterval(recomputeRemaining, 1000);
        pollIntervalRef.current = setInterval(fetchData, STATUS_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            stopTimer();
            stopPolling();
        };
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className={classes.Wrapper}>
                <div className={classes.Card}>
                    <div className={classes.Muted}>Загрузка…</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.Wrapper}>
                <div className={classes.Card}>
                    <div className={classes.StatusTitle}>Ошибка</div>
                    <div className={classes.Muted}>{error}</div>
                </div>
            </div>
        );
    }

    const amountRub = data ? formatMoney(data.amountRub) : null;
    const amountKgs = data ? formatMoney(data.amountKgs) : null;
    const showTimer = remainingSeconds != null && !FINAL_STATUSES.includes(status);

    // Финальные состояния — короткий экран результата.
    if (status === "SUCCESS") {
        return (
            <div className={classes.Wrapper}>
                <div className={classes.Card}>
                    <div className={classes.StatusSuccess}>Оплата прошла успешно</div>
                    {amountRub && <div className={classes.Amount}>{amountRub} ₽</div>}
                </div>
            </div>
        );
    }
    if (status === "DECLINED") {
        return (
            <div className={classes.Wrapper}>
                <div className={classes.Card}>
                    <div className={classes.StatusDeclined}>Оплата отклонена</div>
                    <div className={classes.Muted}>Попробуйте создать платёж заново.</div>
                </div>
            </div>
        );
    }
    if (status === "EXPIRED") {
        return (
            <div className={classes.Wrapper}>
                <div className={classes.Card}>
                    <div className={classes.StatusDeclined}>Срок действия ссылки истёк</div>
                    <div className={classes.Muted}>Запросите новую ссылку для оплаты.</div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.Wrapper}>
            <div className={classes.Card}>
                <div className={classes.Title}>Оплата через Сбербанк по QR-коду</div>

                <div className={classes.Amounts}>
                    {amountRub && <div className={classes.Amount}>{amountRub} ₽</div>}
                    {amountKgs && (
                        <div className={classes.AmountKgs}>≈ {amountKgs} KGS</div>
                    )}
                </div>

                {data && data.qrDataUri ? (
                    <div className={classes.QrBox}>
                        <img className={classes.Qr} src={data.qrDataUri} alt="QR-код для оплаты" />
                    </div>
                ) : (
                    <div className={classes.Muted}>QR-код недоступен</div>
                )}

                {showTimer && (
                    <div className={classes.Timer}>
                        <span className={classes.TimerLabel}>Осталось времени:</span>{" "}
                        <span
                            className={
                                remainingSeconds <= 60
                                    ? classes.TimerValueDanger
                                    : classes.TimerValue
                            }
                        >
                            {formatTime(remainingSeconds)}
                        </span>
                    </div>
                )}

                <div className={classes.Instruction}>
                    <div className={classes.InstructionTitle}>Как оплатить:</div>
                    <ol className={classes.Steps}>
                        <li>Откройте приложение <b>Сбербанк Онлайн</b> на телефоне.</li>
                        <li>Выберите <b>«Оплата по QR или штрихкоду»</b> (значок QR на главном экране или в разделе «Платежи»).</li>
                        <li>Наведите камеру на QR-код выше.</li>
                        <li>Проверьте сумму и подтвердите оплату.</li>
                    </ol>
                    <div className={classes.Note}>
                        Оплата с компьютера: отсканируйте QR-код камерой телефона через приложение банка.
                    </div>
                </div>

                <div className={classes.Waiting}>Ожидаем поступление оплаты…</div>
            </div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/PagesOfCommon/NPay/index.jsx